// Draft preservation (SPEC.md §6): never lose a half-typed ticket to a navigation
// or a dropped session mid-call.
//
// localStorage rather than the server: the draft has to survive a reload and a
// session that expired while the agent was on the phone, and the API has no draft
// endpoint. It is one slot, not a list — an agent handles one call at a time, and
// a queue of stale drafts would be worse than none.
//
// Every access is wrapped: localStorage throws in private windows and when the
// origin's quota is full, and losing a draft is not worth taking the screen down.

const STORAGE_KEY = 'crm.ticket-draft.v1';

// A month-old draft is a mis-restore, not a rescue.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function loadDraft() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    if (!stored?.draft) return null;
    if (typeof stored.saved_at !== 'number' || Date.now() - stored.saved_at > MAX_AGE_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return { ...stored.draft, saved_at: stored.saved_at };
  } catch {
    return null;
  }
}

export function saveDraft(draft) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ saved_at: Date.now(), draft }));
  } catch {
    // Nothing useful to do: the form still works, the draft just is not insured.
  }
}

export function clearDraft() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // See saveDraft.
  }
}

/**
 * Whether a draft is worth keeping. A form the agent only opened — default form,
 * default queue, nothing typed — must not resurrect itself tomorrow as a "restored
 * draft" banner.
 */
export function draftHasContent({ caller, fields, composer } = {}, { defaultAssigneeId = '' } = {}) {
  if (caller?.customer || caller?.organization) return true;
  if (composer?.subject?.trim() || composer?.body_text?.trim()) return true;
  if (fields?.tags?.length || fields?.follower_user_ids?.length || fields?.cc_customer_ids?.length) return true;
  if (fields?.device_serial?.trim()) return true;
  // The assignee now defaults to the current user (docs/API.md §3.3), so a form that
  // was merely opened carries one. Only a CHANGED owner is content — otherwise every
  // visit would resurrect yesterday's untouched form as a "restored draft".
  if (fields?.assigned_user_id && String(fields.assigned_user_id) !== String(defaultAssigneeId)) return true;
  if (fields?.links?.length || fields?.parent || fields?.children?.length) return true;
  if (fields?.custom_fields && Object.values(fields.custom_fields).some(hasValue)) return true;
  return false;
}

function hasValue(value) {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (value === false) return false;
  return true;
}
